import { type Component } from 'vue';

import DropdownOptions from '@/components/DropdownOptions/DropdownOptions.vue';
import GalleryOptions from '@/components/GalleryOptions/GalleryOptions.vue';
import { mountCoreVueApp } from '@/entrypoints/helpers/core-vue-app';
import {
  inventoryManagement,
  inventoryPolicy
} from '@/shared/constants/shopify';
import GlobalKeyDownDirective from '@/shared/directives/globalKeyDown';
import { useProductStore } from '@/shared/pinia/modules/product';
import { useProductOptionsStore } from '@/shared/pinia/modules/product-options.ts';
import { notifyBugsnagWithContext } from '@/shared/utils/error.ts';

const initOptions = (mountId: string) => {
  const script = document.getElementById(mountId);

  if (!script) {
    console.error(`Element with id: "${mountId}" not found.`);
    return;
  }

  const {
    optionKind,
    optionValue,
    variantsStock,
    inventoryPolicyInfo,
    inventoryManagementInfo,
    variantColorCodes,
    variantDiscontinuedStatus,
    variantsDisabled,
    variantKindTranslation
  } = script.dataset;

  const kindObject = JSON.parse(optionKind!);
  const kind = variantKindTranslation
    ? { ...kindObject, name: variantKindTranslation }
    : kindObject;

  let options = JSON.parse(optionValue!);
  const variantsStockData = JSON.parse(variantsStock!);
  const inventoryPolicyData = JSON.parse(inventoryPolicyInfo!);
  const inventoryManagementData = JSON.parse(inventoryManagementInfo!);
  const colorCodesData = JSON.parse(variantColorCodes!);
  const discontinuedStatusData = JSON.parse(variantDiscontinuedStatus!);
  const disabledData = JSON.parse(variantsDisabled!);
  const selectedOption =
    options.length === 1
      ? options[0]
      : JSON.parse(script.dataset.selectedOption!);

  const productStore = useProductStore();

  options = options
    .map(
      (
        option: ShopifyVariant,
        index: number,
        allVariants: ShopifyVariant[]
      ) => {
        // handle prices
        const priceBefore = option.compare_at_price || option.price;

        // handle colorCode
        const colorCode = colorCodesData[index];

        const disabled = disabledData[index];

        // handle discontinued status
        const discontinued = discontinuedStatusData[index];

        // handle consolidated image: using a fallback image in case variant image is missing
        const consolidatedImageUrl =
          option.featured_image?.src ||
          (allVariants.length === 1 && productStore.mainImageUrl) ||
          window.Hobbii?.fallbackImageUrl;

        // handle stock
        const variantStock = variantsStockData[index];
        const policyData = inventoryPolicyData[index];
        const inventoryType = inventoryManagementData[index];

        const inStock =
          inventoryType == inventoryManagement.TRACKING
            ? variantStock > 0 || policyData === inventoryPolicy.CONTINUE
            : true;

        const stockData = {
          inStock,
          stockData: variantStock,
          inventory_policy: policyData,
          inventory_management: inventoryType
        };

        return {
          ...option,
          priceBefore,
          disabled,
          colorCode,
          ...stockData,
          discontinued,
          consolidatedImageUrl
        };
      }
    )
    .filter((variant: HydratedShopifyVariant) => {
      const nonDiscontinued = !variant.discontinued;
      const nonDisabled = !variant.disabled;

      // keep also discontinued variants that are in stock
      return nonDisabled && (nonDiscontinued || variant.inStock);
    });

  try {
    options = options.sort(
      (a: HydratedShopifyVariant, b: HydratedShopifyVariant) =>
        Number.parseInt(a.colorCode, 10) - Number.parseInt(b.colorCode, 10)
    );
  } catch (e) {
    console.error('Sorting failed due to error', e);
  }

  if (options.length > 1) {
    for (const option of options) {
      if (!option.featured_image?.src) {
        notifyBugsnagWithContext(
          'product-options.js - initOptions',
          `[DATA-ERROR] - Missing variant image: 'featured_image' for "${option.name}" (sku: "${option.sku}").`
        );
      }
    }
  }

  const productOptionsStore = useProductOptionsStore();
  productOptionsStore.$patch({ options, kind });

  if (selectedOption?.id) {
    productOptionsStore.activeOption = options.find(
      (variant: HydratedShopifyVariant) => variant.id === selectedOption.id
    );
  }
};

const initVueApp = async (mountId: string, component: Component) => {
  await mountCoreVueApp(
    component,
    `#${mountId}`,
    {},
    (app) => {
      app.directive('global-keydown', GlobalKeyDownDirective);
    },
    { noClients: true }
  );
};

const initGalleryOptions = (mountId: string) => {
  initOptions(mountId);
  initVueApp(mountId, GalleryOptions);
};

const initDropdownOptions = (mountId: string) => {
  initOptions(mountId);
  initVueApp(mountId, DropdownOptions);
};

const initSingleOption = (mountId: string) => {
  initOptions(mountId);
};

export { initDropdownOptions, initGalleryOptions, initSingleOption };
